import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { BomHeader } from './bomHeaderApi';
import { RawItem } from './rawItemApi';
import { HsnCode } from './hsnApi';
import { ItemCategory } from './itemCategoryApi';
import { Process } from './processesApi';
import { Endgood } from './endgoodApi';

export interface BomDetail {
  id: number;
  quantity: number;
  unit: string; // Unit of measurement (e.g., kg, pcs, m)
  unitCost: number;
  totalCost: number;
  wastagePercentage: number;
  wastageQuantity: number;
  totalQuantity: number;
  remarks: string;
  isCritical: boolean;
  leadTimeDays: number;
  priority?: number;
  branchId: number;
  rawCategoryId: number;
  endgoodId?: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  bomHeader: BomHeader;
  rawItem?: RawItem & {
    itemCategory: ItemCategory;
    hsn: HsnCode;
  };
  endgood?: Endgood;
  rawCategory?: ItemCategory;
  process: Process;
}

export interface BomDetailResponse extends PaginatedResponse<BomDetail> {}

export interface BomDetailCreateRequest {
  quantity: number;
  unit: string; // Unit of measurement (e.g., kg, pcs, m)
  unitCost: number;
  totalCost: number;
  wastagePercentage: number;
  wastageQuantity: number;
  totalQuantity: number;
  remarks: string;
  isCritical: boolean;
  leadTimeDays: number;
  priority?: number;
  branchId: number;
  bomHeaderId: number;
  rawItemId?: number;
  endgoodId?: number;
  rawCategoryId: number;
  processId: number;
  createdBy: number;
  updatedBy: number;
}

export interface BomDetailUpdateRequest extends BomDetailCreateRequest {
  id: number;
}

class BomDetailApiService extends BaseApiService {
  async getBomDetails(): Promise<BomDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail[]>>(
        API_ENDPOINTS.BOM_DETAILS.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch BOM details');
    } catch (error) {
      // console.error('Error fetching BOM details:', error);
      toast.error('Failed to fetch BOM details');
      throw error;
    }
  }

  async getBomDetailById(id: string): Promise<BomDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail>>(
        API_ENDPOINTS.BOM_DETAILS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch BOM detail');
      }
    } catch (error) {
      // console.error('Error fetching BOM detail:', error);
      toast.error('Failed to fetch BOM detail details');
      throw error;
    }
  }

  async getBomDetailsByHeader(bomHeaderId: number): Promise<BomDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail[]>>(
        `${API_ENDPOINTS.BOM_HEADERS.BOM_DETAIL(bomHeaderId.toString())}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch BOM details for header');
      }
    } catch (error) {
      // console.error('Error fetching BOM details for header:', error);
      toast.error('Failed to fetch BOM details for header');
      throw error;
    }
  }

  async getBomDetailsByRawItem(rawItemId: number): Promise<BomDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail[]>>(
        `${API_ENDPOINTS.BOM_DETAILS.LIST}?rawItemId=${rawItemId}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch BOM details for raw item');
      }
    } catch (error) {
      // console.error('Error fetching BOM details for raw item:', error);
      toast.error('Failed to fetch BOM details for raw item');
      throw error;
    }
  }

  async createBomDetail(bomDetailData: BomDetailCreateRequest): Promise<BomDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail>>(
        API_ENDPOINTS.BOM_DETAILS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(bomDetailData)
        }
      );
      
      if (response.success) {
        toast.success('BOM detail created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create BOM detail');
      }
    } catch (error) {
      // console.error('Error creating BOM detail:', error);
      toast.error('Failed to create BOM detail');
      throw error;
    }
  }

  async updateBomDetail(id: string, bomDetailData: BomDetailUpdateRequest): Promise<BomDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail>>(
        API_ENDPOINTS.BOM_DETAILS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(bomDetailData)
        }
      );
      
      if (response.success) {
        toast.success('BOM detail updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update BOM detail');
      }
    } catch (error) {
      // console.error('Error updating BOM detail:', error);
      toast.error('Failed to update BOM detail');
      throw error;
    }
  }

  async deleteBomDetail(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.BOM_DETAILS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('BOM detail deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete BOM detail');
      }
    } catch (error) {
      // console.error('Error deleting BOM detail:', error);
      toast.error('Failed to delete BOM detail');
      throw error;
    }
  }

  async searchBomDetails(searchTerm: string): Promise<BomDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail[]>>(
        `${API_ENDPOINTS.BOM_DETAILS.SEARCH}?q=${encodeURIComponent(searchTerm)}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search BOM details');
      }
    } catch (error) {
      // console.error('Error searching BOM details:', error);
      toast.error('Failed to search BOM details');
      throw error;
    }
  }

  async createMultipleBomDetails(bomDetailsData: BomDetailCreateRequest[]): Promise<BomDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomDetail[]>>(
        `${API_ENDPOINTS.BOM_DETAILS.CREATE}/batch`,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(bomDetailsData)
        }
      );
      
      if (response.success) {
        toast.success(`${bomDetailsData.length} BOM details created successfully`);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create BOM details');
      }
    } catch (error) {
      // console.error('Error creating BOM details:', error);
      toast.error('Failed to create BOM details');
      throw error;
    }
  }
}

export const bomDetailApiService = new BomDetailApiService();
